import React, { createContext, useContext, useState, useEffect } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';

// Define role types
export type UserRole = 'admin' | 'editor' | 'guest_coordinator' | 'viewer';

// Define permission types
export type Permission = 
  | 'canManageGuests' 
  | 'canManageNominations' 
  | 'canManageApplications' 
  | 'canViewMap'
  | 'canManageUsers'
  | 'canAccessAdmin';

// Define context type
interface AuthContextType {
  user: ReturnType<typeof useUser>['user'] | null;
  userRole: UserRole | null;
  hasPermission: (permission: Permission) => boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType>({
  user: null,
  userRole: null,
  hasPermission: () => false,
  isLoading: true,
  isAuthenticated: false,
  signOut: async () => {}
});

// Role-based permissions mapping
const rolePermissions: Record<UserRole, Permission[]> = {
  'admin': [
    'canManageGuests', 
    'canManageNominations', 
    'canManageApplications', 
    'canViewMap',
    'canManageUsers',
    'canAccessAdmin'
  ],
  'editor': [
    'canManageGuests', 
    'canManageNominations', 
    'canManageApplications', 
    'canViewMap',
    'canAccessAdmin'
  ],
  'guest_coordinator': [
    'canManageGuests', 
    'canManageNominations', 
    'canManageApplications', 
    'canViewMap',
    'canAccessAdmin'
  ],
  'viewer': [
    'canViewMap',
    'canAccessAdmin'
  ]
};

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoaded } = useUser();
  const clerk = useClerk();
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isAuthenticated = !!user;

  // Get user role from Clerk metadata
  useEffect(() => {
    const getUserRole = async () => {
      if (!isLoaded) return;
      
      if (!user) {
        setUserRole(null);
        setIsLoading(false);
        return;
      }

      try {
        // Get role from public metadata
        const role = user.publicMetadata.role as UserRole;
        setUserRole(role || 'viewer'); // Default to viewer if no role is set
      } catch (error) {
        console.error('Error getting user role:', error);
        setUserRole('viewer'); // Default to viewer on error
      } finally {
        setIsLoading(false);
      }
    };

    getUserRole();
  }, [user, isLoaded]);

  // Check if user has permission
  const hasPermission = (permission: Permission): boolean => {
    if (!userRole) return false;
    return rolePermissions[userRole].includes(permission);
  };

  // Sign out function
  const signOut = async (): Promise<void> => {
    try {
      await clerk.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      userRole, 
      hasPermission, 
      isLoading, 
      isAuthenticated,
      signOut 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuthContext = () => useContext(AuthContext);
